import React from 'react';
import { Box, Alert, Snackbar, Button, useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent, IconButton, Typography, Divider, Chip, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import QuestTabs from '../components/quests/QuestTabs';
import QuestSidebar from '../components/quests/QuestSidebar';
import QuestDetailPanel, { buildQuestPutPayload } from '../components/quests/QuestDetailPanel';
import CreateQuestForm from '../components/quests/CreateQuestForm';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  fetchTickets,
  fetchQuestCountsByStatus,
  fetchStatuses,
  fetchCategories,
  fetchServices,
  getMasters,
  setCurrentTab,
  updateTicketStatus,
  cancelTicket,
} from '../store/slices/questsSlice';
import { ticketService } from '../services/questService';
import { getQuestStatusIndex, QUEST_STATUS } from '../utils/questStatus';
import { fetchAppealDetail } from '../store/slices/appealsSlice';

const getQuestTabFromParams = (searchParams) => {
  const rawTab = searchParams.get('questTab');
  if (rawTab == null || rawTab === '') return null;

  const tabIndex = Number(rawTab);
  if (Number.isInteger(tabIndex) && tabIndex >= 0 && tabIndex <= 4) {
    return tabIndex;
  }

  return getQuestStatusIndex(rawTab);
};

const QuestsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  const { quests, loading, loadingMore, currentTab, error, pagination, hasMore } = useSelector(
    (state) => state.quests
  );

  const [selectedQuest, setSelectedQuest] = React.useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [notification, setNotification] = React.useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [appealModal, setAppealModal] = React.useState({
    open: false,
    appealId: null,
    appeal: null,
    loading: false,
  });
  const [sortOrder, setSortOrder] = React.useState('desc'); // 'desc' - новые сверху, 'asc' - старые сверху
  const beforeQuestChangeRef = React.useRef(null);

  const handleCloseNotification = () =>
    setNotification({ ...notification, open: false });

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  const showNotification = (message, severity = 'info') =>
    setNotification({ open: true, message, severity });

  const refreshStatusCounts = React.useCallback(() => {
    dispatch(fetchQuestCountsByStatus()).unwrap().catch(() => {});
  }, [dispatch]);

  const loadTickets = React.useCallback(
    (page = 1, pageSize = 10, statusCode = currentTab) => {
      dispatch(fetchTickets({ page, pageSize, statusCode }))
        .unwrap()
        .catch((error) =>
          showNotification(`Ошибка загрузки заявок: ${error.message || 'Неизвестная ошибка'}`, 'error')
        );
    },
    [dispatch, currentTab]
  );

  const handleLoadMore = React.useCallback(() => {
    if (hasMore && !loadingMore && !loading) {
      const nextPage = pagination.currentPage + 1;
      loadTickets(nextPage, pagination.pageSize, currentTab);
    }
  }, [hasMore, loadingMore, loading, pagination.currentPage, pagination.pageSize, loadTickets, currentTab]);

  React.useEffect(() => {
    const targetTab = getQuestTabFromParams(searchParams) ?? currentTab;
    if (targetTab !== currentTab) {
      dispatch(setCurrentTab(targetTab));
    }

    refreshStatusCounts();
    loadTickets(1, pagination.pageSize, targetTab);
    dispatch(fetchStatuses()).unwrap().catch(() => {});
    dispatch(fetchCategories()).unwrap().catch(() => {});
    dispatch(fetchServices()).unwrap().catch(() => {});
    dispatch(getMasters()).unwrap().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- начальная загрузка при открытии страницы
  }, [dispatch]);

  React.useEffect(() => {
    const questId = searchParams.get('questId');
    if (!questId || questId === 'undefined') return undefined;

    let cancelled = false;
    const targetTab = getQuestTabFromParams(searchParams);

    const openLinkedQuest = async () => {
      if (targetTab != null && targetTab !== currentTab) {
        dispatch(setCurrentTab(targetTab));
        loadTickets(1, pagination.pageSize, targetTab);
      }

      const match = quests?.find((t) => String(t.id) === String(questId));
      if (match) setSelectedQuest(match);

      try {
        const details = await ticketService.getTicket(questId);
        if (cancelled) return;

        const detailTab = getQuestStatusIndex(details);
        if (detailTab !== currentTab) {
          dispatch(setCurrentTab(detailTab));
          loadTickets(1, pagination.pageSize, detailTab);
        }

        setSelectedQuest(details);
        setSearchParams({}, { replace: true });
      } catch {
        if (cancelled) return;
        if (!match) {
          setNotification({
            open: true,
            message: 'Не удалось загрузить связанную заявку',
            severity: 'error',
          });
        }
        setSearchParams({}, { replace: true });
      }
    };

    openLinkedQuest();
    return () => { cancelled = true; };
  }, [searchParams, quests, currentTab, dispatch, loadTickets, pagination.pageSize, setSearchParams]);

  const handleTabChange = (tabIndex) => {
    dispatch(setCurrentTab(tabIndex));
    loadTickets(1, pagination.pageSize, tabIndex);
    setSelectedQuest(null);
  };

  const reloadListAndCounts = React.useCallback(() => {
    refreshStatusCounts();
    loadTickets(pagination.currentPage, pagination.pageSize, currentTab);
  }, [refreshStatusCounts, loadTickets, pagination.currentPage, pagination.pageSize, currentTab]);

  const reloadQuestDetails = async () => {
    if (!selectedQuest?.id) return;
    try {
      const details = await ticketService.getTicket(selectedQuest.id);
      setSelectedQuest(details);
    } catch {
      // fallback
    }
  };

  const loadQuestDetails = async (quest) => {
    setSelectedQuest(quest);
    try {
      const details = await ticketService.getTicket(quest.id);
      setSelectedQuest(details);
    } catch {
      // fallback — данные из списка
    }
  };

  const handleSelectQuest = async (quest) => {
    if (beforeQuestChangeRef.current) {
      const canProceed = await beforeQuestChangeRef.current(quest);
      if (!canProceed) return;
    }
    await loadQuestDetails(quest);
  };

  const handleAssign = async (quest) => {
    try {
      const ticketData = buildQuestPutPayload(quest, { status: QUEST_STATUS.ASSIGNED });
      await dispatch(updateTicketStatus({ ticketId: quest.id, ticketData })).unwrap();
      showNotification(`Заявка №${quest.number || quest.id} назначена`, 'success');
      await reloadQuestDetails();
      reloadListAndCounts();
    } catch (error) {
      showNotification(`Ошибка: ${error.message || 'Неизвестная ошибка'}`, 'error');
      throw error;
    }
  };

  const handleCancel = async (quest) => {
    try {
      await dispatch(cancelTicket(quest.id)).unwrap();
      showNotification(`Заявка №${quest.number || quest.id} отменена`, 'success');
      await reloadQuestDetails();
      reloadListAndCounts();
    } catch (error) {
      showNotification(`Ошибка: ${error.message || 'Неизвестная ошибка'}`, 'error');
      throw error;
    }
  };

  const handleSave = async (questId, ticketData) => {
    try {
      await dispatch(updateTicketStatus({ ticketId: questId, ticketData })).unwrap();
      showNotification('Заявка сохранена', 'success');
      await reloadQuestDetails();
      reloadListAndCounts();
    } catch (error) {
      showNotification(`Ошибка: ${error.message || 'Неизвестная ошибка'}`, 'error');
      throw error;
    }
  };

  const handleExecutorChange = async (questId, masterId) => {
    if (!selectedQuest || selectedQuest.id !== questId) return;
    try {
      const ticketData = buildQuestPutPayload(selectedQuest, {
        masterId: masterId || '00000000-0000-0000-0000-000000000000',
      });
      await dispatch(updateTicketStatus({ ticketId: questId, ticketData })).unwrap();
      showNotification('Исполнитель обновлён', 'success');
      await reloadQuestDetails();
      reloadListAndCounts();
    } catch (error) {
      showNotification(`Ошибка: ${error.message || 'Неизвестная ошибка'}`, 'error');
      throw error;
    }
  };

  const handleCreateQuest = () => {
    showNotification('Заявка успешно создана', 'success');
    reloadListAndCounts();
  };

  const handleViewAppeal = async (appealId) => {
    setAppealModal({ open: true, appealId, appeal: null, loading: true });
    try {
      const result = await dispatch(fetchAppealDetail({ ticketId: appealId })).unwrap();
      setAppealModal(prev => ({ ...prev, appeal: result, loading: false }));
    } catch {
      showNotification('Не удалось загрузить обращение', 'error');
      setAppealModal({ open: false, appealId: null, appeal: null, loading: false });
    }
  };

  const handleCloseAppealModal = () => {
    setAppealModal({ open: false, appealId: null, appeal: null, loading: false });
  };

  const handleOpenAppealPage = () => {
    const appealId = appealModal.appeal?.Id || appealModal.appealId;
    if (!appealId) return;

    const params = new URLSearchParams({ appealId });
    navigate(`/appeals?${params.toString()}`);
  };

  const formatAppealDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  const sortedQuests = React.useMemo(() => {
    if (!quests || quests.length === 0) return [];
    return [...quests].sort((a, b) => {
      const dateA = new Date(a.createdAt || a.createdDate || 0);
      const dateB = new Date(b.createdAt || b.createdDate || 0);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [quests, sortOrder]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? 'auto' : 'calc(100vh - 132px)',
        overflow: 'hidden',
      }}
    >
      {/* Error banner */}
      {error && (
        <Alert severity="error" sx={{ m: 2, mb: 0 }}>
          {error}
        </Alert>
      )}

      {/* Top: Tabs + Create button */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pt: 1 }}>
        <Box sx={{ flex: 1 }}>
          <QuestTabs
            tabValue={currentTab}
            onTabChange={handleTabChange}
          />
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={() => setIsCreateModalOpen(true)}
          startIcon={<AddIcon />}
          sx={{ ml: 2, textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap' }}
        >
          Создать
        </Button>
      </Box>

      {/* Two-panel content */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        {/* Left sidebar - Ticket list */}
        <Box
          sx={{
            width: isMobile ? '100%' : 320,
            minWidth: isMobile ? '100%' : 280,
            maxWidth: isMobile ? '100%' : 360,
            borderRight: isMobile ? 'none' : '1px solid',
            borderBottom: isMobile ? '1px solid' : 'none',
            borderColor: 'divider',
            bgcolor: 'grey.50',
            height: isMobile ? 300 : '100%',
            overflow: 'hidden',
          }}
        >
          <QuestSidebar
            quests={sortedQuests}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedQuestId={selectedQuest?.id}
            onSelect={handleSelectQuest}
            onViewAppeal={handleViewAppeal}
            onLoadMore={handleLoadMore}
            sortOrder={sortOrder}
            onSortToggle={toggleSortOrder}
          />
        </Box>

        {/* Center - Ticket detail */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            bgcolor: '#fff',
            height: isMobile ? 'auto' : '100%',
            minHeight: isMobile ? 400 : 0,
          }}
        >
          <QuestDetailPanel
            quest={selectedQuest}
            currentTab={currentTab}
            onSave={handleSave}
            onAssign={handleAssign}
            onCancel={handleCancel}
            onExecutorChange={handleExecutorChange}
            onQuestChanged={reloadQuestDetails}
            onNotify={showNotification}
            beforeQuestChangeRef={beforeQuestChangeRef}
          />
        </Box>
      </Box>

      {/* Create quest modal */}
      <CreateQuestForm
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onQuestCreated={handleCreateQuest}
      />

      {/* Appeal detail modal */}
      <Dialog
        open={appealModal.open}
        onClose={handleCloseAppealModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            Обращение #{appealModal.appeal?.Number || appealModal.appealId?.slice(0, 8)}
          </Typography>
          <IconButton onClick={handleCloseAppealModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {appealModal.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : appealModal.appeal ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {appealModal.appeal.Category?.Name && (
                <Chip label={appealModal.appeal.Category.Name} color="primary" size="small" />
              )}
              <Typography variant="caption" color="text.secondary">
                Создано: {formatAppealDate(appealModal.appeal.CreatedAt)}
              </Typography>

              <Divider />

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                {appealModal.appeal.OwnerName && (
                  <Typography variant="body2">
                    <strong>Владелец:</strong> {appealModal.appeal.OwnerName}
                  </Typography>
                )}
                {appealModal.appeal.PhoneNumber && (
                  <Typography variant="body2">
                    <strong>Телефон:</strong> {appealModal.appeal.PhoneNumber}
                  </Typography>
                )}
              </Box>

              {appealModal.appeal.FirstMessage && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Первое сообщение</Typography>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {appealModal.appeal.FirstMessage}
                    </Typography>
                  </Box>
                </Box>
              )}

              {appealModal.appeal.Comment && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Комментарий</Typography>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {appealModal.appeal.Comment}
                    </Typography>
                  </Box>
                </Box>
              )}
              <Button
                variant="outlined"
                fullWidth
                onClick={handleOpenAppealPage}
                disabled={!appealModal.appealId && !appealModal.appeal?.Id}
                sx={{
                  mt: 1,
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  color: 'text.secondary',
                  borderColor: 'grey.400',
                  '&:hover': {
                    borderColor: 'grey.600',
                    bgcolor: 'grey.100',
                  },
                }}
              >
                К обращению
              </Button>
            </Box>
          ) : (
            <Typography color="text.secondary">Не удалось загрузить обращение</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuestsPage;
